import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { setSentryUser } from "../sentry";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);

  // Keep Sentry user context in sync with auth state. When the user logs out
  // (false) or session check is in flight (null), Sentry context is cleared.
  useEffect(() => {
    setSentryUser(user);
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    // Backend register returns a generic message in both branches (new
    // account / existing email) to prevent enumeration. We rely on whether
    // /auth/me succeeds afterwards to know if cookies were actually set.
    await api.post("/auth/register", { name, email, password });
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return { status: "active", user: data };
    } catch {
      // Existing email branch — no cookies were set on register; treat as
      // "check your email" and let the UI prompt the user to sign in.
      setUser(false);
      return { status: "pending" };
    }
  };

  const loginWithGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  const logoutEverywhere = async () => {
    // Revoke every active refresh token this user owns on the server AND
    // clear cookies on this device. The response body carries the count of
    // sessions revoked so the caller can show a toast; we don't await it in
    // the local state update because setUser(false) drops us to the login
    // screen either way.
    try {
      const { data } = await api.post("/auth/logout-everywhere");
      setUser(false);
      return data;
    } catch (err) {
      // Fail-open: if the request fails (e.g. offline), still drop the
      // local session so the user isn't stuck on a broken screen.
      setUser(false);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, logoutEverywhere, loginWithGoogle, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
